import { format } from 'date-fns';
import { type ComponentProps, memo, useCallback, useMemo } from 'react';

import { ClockIcon } from '@air/design-icons';
import { Avatar } from '@air/primitive-avatar';
import { HoverCard } from '@air/primitive-hover-card';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { formatDuration, formatFullName, parseAirDateToISO } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { usePlayerStore } from './PlayerContextProvider';
import { type Marker } from './PlayerSeekbarTimestamps';

const PLAYER_SEEKBAR_TIMESTAMP_MARKER_HOVER_CARD_DELAY_IN_MS = 300;
const PERCENTAGE_MULTIPLIER = 100;

const formatMarkerTimestamp = (marker: Marker) =>
  marker.endTimestamp === undefined
    ? formatDuration(Math.round(marker.timestamp))
    : `${formatDuration(Math.round(Math.min(marker.timestamp, marker.endTimestamp)))} - ${formatDuration(
        Math.round(Math.max(marker.timestamp, marker.endTimestamp)),
      )}`;

export type PlayerSeekbarTimestampsMarkerProps = ComponentProps<'div'> & {
  disabled?: boolean;
  highlight?: boolean;
  marker: Marker;
  onHoverChange?: (marker: Marker | null) => void;
  onMarkerSelect?: (marker: Marker) => void;
};

export const PlayerSeekbarTimestampsMarker = memo(
  ({
    className,
    disabled = false,
    highlight = false,
    marker,
    onHoverChange,
    onMarkerSelect,
    ...restOfProps
  }: PlayerSeekbarTimestampsMarkerProps) => {
    const duration = usePlayerStore((state) => state.duration);

    const handleClick = useCallback(() => {
      if (disabled) {
        return;
      }

      onMarkerSelect?.(marker);
    }, [disabled, marker, onMarkerSelect]);

    const topLevelComment = useMemo(() => {
      return marker.discussion.comments.data.at(0);
    }, [marker.discussion]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        onHoverChange?.(open ? marker : null);
      },
      [marker, onHoverChange],
    );

    const trigger = useMemo(
      () => (
        <button
          className={cn(
            'focus-visible:focus absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full outline-none ring-2 ring-transparent transition-[box-shadow] hover:ring-white/80 data-[highlight=true]:ring-white/80',
            disabled ? 'pointer-events-none cursor-default' : 'pointer-events-auto',
          )}
          data-highlight={highlight ? 'true' : 'false'}
          data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER_BUTTON"
          disabled={disabled}
          onClick={handleClick}
          type="button"
        >
          <VisuallyHidden>Jump to {formatDuration(marker.timestamp)}</VisuallyHidden>
          <Avatar
            className={cn(disabled ? 'cursor-default' : 'cursor-pointer')}
            colorSeed={marker.userId}
            data-commenter-id={marker.userId}
            data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER_AVATAR"
            size={20}
            src={marker.userAvatar}
            text={marker.initials}
          />
        </button>
      ),
      [disabled, handleClick, highlight, marker.initials, marker.timestamp, marker.userAvatar, marker.userId],
    );

    return (
      <div
        className={cn('absolute inset-y-0', className)}
        data-testid="PLAYER_SEEKBAR_TIMESTAMP_MARKER"
        style={{
          left: duration > 0 ? `${(marker.timestamp / duration) * PERCENTAGE_MULTIPLIER}%` : 0,
        }}
        {...restOfProps}
      >
        {disabled ? (
          trigger
        ) : (
          <HoverCard
            className="p-4"
            onOpenChange={handleOpenChange}
            openDelay={PLAYER_SEEKBAR_TIMESTAMP_MARKER_HOVER_CARD_DELAY_IN_MS}
            side="top"
            trigger={trigger}
          >
            <div className="flex w-72 flex-col gap-1">
              <header className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar colorSeed={marker.userId} size={20} src={marker.userAvatar} text={marker.initials} />
                  <div className="truncate font-medium ads:text-primary text-12 text-gray-12">
                    {formatFullName(topLevelComment?.account.firstName, topLevelComment?.account.lastName)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {topLevelComment?.createdAt && (
                    <time className="ads:text-metadata text-12 text-gray-10" dateTime={topLevelComment.createdAt}>
                      {format(parseAirDateToISO(topLevelComment.createdAt), 'MMM dd')}
                    </time>
                  )}
                  <div className="px-1 font-bold ads:text-metadata text-10 text-gray-9 tabular-nums">
                    V{marker.discussion.assetVersion}
                  </div>
                </div>
              </header>
              <div className="flex items-center gap-0.5 pl-7 ads:text-metadata text-10 text-gray-10">
                <ClockIcon size="xs" />
                <time className="tabular-nums" dateTime={marker.timestamp.toString()}>
                  {formatMarkerTimestamp(marker)}
                </time>
              </div>
              <p className="whitespace-pre-line pl-7 ads:text-primary text-14 text-gray-12">{topLevelComment?.body}</p>
            </div>
          </HoverCard>
        )}
      </div>
    );
  },
);

PlayerSeekbarTimestampsMarker.displayName = 'PlayerSeekbarTimestampsMarker';
